import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  attachmentsApi,
  Attachment,
  PresignUploadResponse,
} from '@/services/api/attachmentsApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';

export const attachmentsKeys = {
  all: ['attachments'] as const,
  list: (patientId: string) => [...attachmentsKeys.all, 'list', patientId] as const,
  detail: (id: string) => [...attachmentsKeys.all, 'detail', id] as const,
};

export const useAttachments = (patientId: string | undefined) =>
  useQuery({
    queryKey: attachmentsKeys.list(patientId ?? ''),
    queryFn: () => attachmentsApi.list(patientId as string),
    enabled: !!patientId,
    staleTime: 30_000,
  });

const isDicomFile = (file: File): boolean => {
  const lcName = file.name.toLowerCase();
  return (
    file.type.startsWith('application/dicom') ||
    lcName.endsWith('.dcm') ||
    lcName.endsWith('.dicom')
  );
};

const inferMimeType = (file: File): string => {
  if (file.type) return file.type;
  if (isDicomFile(file)) return 'application/dicom';
  return 'application/octet-stream';
};

interface UploadOpts {
  patientId: string;
  file: File;
  medicalRecordId?: string;
  onProgress?: (loaded: number, total: number) => void;
}

/**
 * Two-phase upload:
 *   1. presign — server creates a `pending` Attachment row and returns
 *      a short-lived signed URL plus the SSE-KMS headers we have to
 *      echo on the PUT
 *   2. PUT to MinIO directly — bytes never traverse the backend
 *   3. finalize — flip the row to `ready` and persist DICOM metadata
 *      if applicable
 */
export const uploadAttachment = async ({
  patientId,
  file,
  medicalRecordId,
  onProgress,
}: UploadOpts): Promise<Attachment> => {
  const mimeType = inferMimeType(file);
  const presign: PresignUploadResponse = await attachmentsApi.presign(patientId, {
    originalName: file.name,
    mimeType,
    sizeBytes: file.size,
    medicalRecordId,
  });

  const etag = await new Promise<string | null>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.uploadUrl, true);
    for (const [k, v] of Object.entries(presign.requiredHeaders)) {
      xhr.setRequestHeader(k, v);
    }
    if (onProgress) {
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) onProgress(ev.loaded, ev.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // ETag header may be quoted; strip the quotes for cleaner display.
        const raw = xhr.getResponseHeader('ETag') ?? null;
        resolve(raw ? raw.replace(/^"|"$/g, '') : null);
      } else {
        reject(new Error(`MinIO PUT failed: HTTP ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during attachment upload'));
    xhr.send(file);
  });

  let dicomMetadata: Record<string, unknown> | undefined;
  if (isDicomFile(file)) {
    try {
      dicomMetadata = await extractDicomTags(file);
    } catch (err) {
      console.warn('Failed to parse DICOM tags', err);
    }
  }

  return attachmentsApi.finalize(presign.attachmentId, {
    etag: etag ?? undefined,
    dicomMetadata,
  });
};

/**
 * VR types whose values are binary blobs (or nested sequences) — never
 * useful as JSONB strings, and `ds.string()` returns garbage or throws on
 * them. Skipped in the full-header dump.
 */
const BINARY_VRS = new Set(['OB', 'OW', 'OF', 'OD', 'OL', 'OV', 'UN', 'SQ', 'US', 'SS', 'AT']);
/** Pixel data — the image itself, not metadata. Stays in MinIO. */
const PIXEL_DATA_TAG = 'x7fe00010';
/** Per-value cap so a stray long element can't bloat the JSONB row. */
const MAX_VALUE_LEN = 256;

/**
 * Pulls DICOM metadata for an uploaded study. Two layers:
 *
 *  - **Named fields** — the curated subset the chart and viewer header read
 *    by name (patient, study, series + interop fields like accession,
 *    referring physician, body part).
 *  - **`tags`** — the full readable header as a `tag → string` map, so the
 *    raw DICOM header is queryable/interoperable later without re-fetching
 *    the file. Pixel data and binary/sequence VRs are excluded; values are
 *    trimmed, length-capped, and non-printables dropped.
 *
 * The pixel data itself stays in MinIO — we only persist text metadata.
 */
const extractDicomTags = async (file: File): Promise<Record<string, unknown>> => {
  const { default: dicomParser } = await import('dicom-parser');
  const buf = new Uint8Array(await file.arrayBuffer());
  const ds = dicomParser.parseDicom(buf);
  const get = (tag: string): string | undefined => {
    try {
      return ds.elements[tag] ? ds.string(tag) : undefined;
    } catch {
      return undefined;
    }
  };

  // Full readable header — every element we can safely stringify.
  const tags: Record<string, string> = {};
  for (const tag of Object.keys(ds.elements)) {
    if (tag === PIXEL_DATA_TAG) continue;
    // Group-length elements (xGGGG0000) — bookkeeping, not data.
    if (tag.length === 9 && tag.slice(5) === '0000') continue;
    const el = ds.elements[tag];
    if (!el || el.length === 0) continue;
    if (el.vr && BINARY_VRS.has(el.vr)) continue;
    let value: string | undefined;
    try {
      value = ds.string(tag);
    } catch {
      continue;
    }
    if (value == null) continue;
    value = value.trim();
    if (!value) continue;
    // Implicit-VR datasets carry no `vr`, so binary can slip through — drop
    // anything with control chars rather than store mojibake.
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x08\x0b\x0c\x0e-\x1f]/.test(value)) continue;
    if (value.length > MAX_VALUE_LEN) value = value.slice(0, MAX_VALUE_LEN);
    tags[tag] = value;
  }

  return {
    // Curated named fields — unchanged keys for back-compat with the viewer.
    patientName: get('x00100010'),
    patientId: get('x00100020'),
    studyDate: get('x00080020'),
    modality: get('x00080060'),
    studyDescription: get('x00081030'),
    seriesDescription: get('x0008103e'),
    studyInstanceUID: get('x0020000d'),
    seriesInstanceUID: get('x0020000e'),
    sopInstanceUID: get('x00080018'),
    // Added interop-relevant named fields (the non-AI value of the header).
    accessionNumber: get('x00080050'),
    referringPhysician: get('x00080090'),
    institutionName: get('x00080080'),
    bodyPart: get('x00180015'),
    manufacturer: get('x00080070'),
    modelName: get('x00081090'),
    stationName: get('x00081010'),
    studyId: get('x00200010'),
    seriesNumber: get('x00200011'),
    instanceNumber: get('x00200013'),
    studyTime: get('x00080030'),
    patientBirthDate: get('x00100030'),
    patientSex: get('x00100040'),
    // Full readable header dump.
    tags,
  };
};

interface UploadAttachmentsArgs {
  patientId: string;
  files: File[];
  /** Link every uploaded file to this encounter (medical record). */
  medicalRecordId?: string;
  onProgress?: (loaded: number, total: number) => void;
}

export const useUploadAttachments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      patientId,
      files,
      medicalRecordId,
      onProgress,
    }: UploadAttachmentsArgs) => {
      const results: Attachment[] = [];
      for (const file of files) {
        const result = await uploadAttachment({
          patientId,
          file,
          medicalRecordId,
          onProgress,
        });
        results.push(result);
      }
      return results;
    },
    onSuccess: (uploaded, vars) => {
      queryClient.invalidateQueries({ queryKey: attachmentsKeys.list(vars.patientId) });
      const dicomCount = uploaded.filter((a) => a.kind === 'dicom').length;
      const imgCount = uploaded.filter((a) => a.kind === 'image').length;
      const otherCount = uploaded.length - dicomCount - imgCount;
      const bits: string[] = [];
      if (imgCount) bits.push(`${imgCount} image${imgCount > 1 ? 's' : ''}`);
      if (dicomCount) bits.push(`${dicomCount} DICOM`);
      if (otherCount) bits.push(`${otherCount} file${otherCount > 1 ? 's' : ''}`);
      toast.success(`Uploaded ${bits.join(', ')}`);
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err) || 'Upload failed');
    },
  });
};

export const useDeleteAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachment: Attachment) => attachmentsApi.remove(attachment.id),
    onSuccess: (_, attachment) => {
      queryClient.invalidateQueries({ queryKey: attachmentsKeys.list(attachment.patientId) });
      toast.success('Attachment deleted');
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err) || 'Delete failed');
    },
  });
};

export const useAttachmentDownloadUrl = () =>
  useMutation({
    mutationFn: (input: { id: string; inline?: boolean }) =>
      attachmentsApi.downloadUrl(input.id, { inline: input.inline }),
  });

/**
 * Run OCR on an image / PDF attachment. Returns the extracted text +
 * per-line confidence + detected language. No DB writes — the caller
 * surfaces the result and the user copies it.
 */
export const useAttachmentOcr = () =>
  useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.ocr(attachmentId),
    onError: (err) => {
      toast.error(extractErrorMessage(err) || 'OCR failed');
    },
  });
